import { useState, useRef } from "react";

import { Stack, Box, ClickAwayListener } from "@mui/material";
import { hexColorValidationRegex } from "@simplyk/common";
import { HuePicker as ReactColorHuePicker } from "react-color";

import { LiveEditorColorButton } from "../LiveEditorColorButton/LiveEditorColorButton";
import { LIVE_EDITOR_BLACK_COLOR, LIVE_EDITOR_PRESET_COLORS } from "../liveEditorColors";

import Pointer from "./pointer.svg";

import { useTranslate } from "@/common/hooks/useTranslate";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LIVE_FORM_Z_INDEX } from "@/components/LiveEditorElements/liveFormZIndex";
import { OrDivider } from "@/components/OrDivider/OrDivider";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

interface LiveEditorSelectColorCardProps {
  formColor: string;
  setFormColor: (color: string) => void;
  setColorToShowInPreview?: (color: string) => void;
  onCustomColorCancel?: () => void;
}

export const LiveEditorSelectColorCard = ({
  formColor,
  setFormColor,
  setColorToShowInPreview,
  onCustomColorCancel,
}: LiveEditorSelectColorCardProps) => {
  const { t } = useTranslate();

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customTempColor, setCustomTempColor] = useState("#0ACE88");
  const gradientButtonRef = useRef<HTMLDivElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const [pointerOffset, setPointerOffset] = useState(0);

  const handleGradientClick = () => {
    setShowColorPicker(true);
    if (formColor && formColor.startsWith("#")) {
      setCustomTempColor(formColor);
    }

    // We'll calculate the pointer position in useEffect after rendering
    setTimeout(() => {
      if (gradientButtonRef.current && colorPickerRef.current) {
        const buttonRect = gradientButtonRef.current.getBoundingClientRect();
        const pickerRect = colorPickerRef.current.getBoundingClientRect();

        // Calculate the center of the gradient button
        const buttonCenter = buttonRect.left + buttonRect.width / 2;
        // Calculate the left edge of the picker
        const pickerLeft = pickerRect.left;

        // Calculate how far from the left edge of the picker the pointer should be
        const offset = buttonCenter - pickerLeft;
        setPointerOffset(offset);
      }
    }, 0);
  };

  const handleColorChange = (color: string) => {
    setFormColor(color);
    setShowColorPicker(false);
  };

  const handleApplyCustomColor = () => {
    handleColorChange(customTempColor);
    setShowColorPicker(false);
  };

  const handleCancelCustomColor = () => {
    setShowColorPicker(false);
    setColorToShowInPreview?.(formColor);
    onCustomColorCancel?.();
  };

  const handleClickAway = () => {
    setShowColorPicker(false);
    setColorToShowInPreview?.(formColor);
  };

  const handlePickerSlide = (color: { hex: string }) => {
    setCustomTempColor(color.hex);
    setColorToShowInPreview?.(color.hex);
  };

  const handlePickerChangeCompleted = (color: { hex: string }) => {
    setCustomTempColor(color.hex);
  };

  // Check if the gradient button should be selected
  const isGradientSelected =
    showColorPicker || // Show as selected when color picker is open
    formColor === "gradient" ||
    !!(formColor && !LIVE_EDITOR_PRESET_COLORS.includes(formColor) && formColor !== LIVE_EDITOR_BLACK_COLOR);

  const isInvalidHex = !hexColorValidationRegex.test(customTempColor);

  return (
    <LiveEditorBlock>
      <Stack gap={1.5} sx={{ position: "relative" }}>
        <DashboardTypography variant="subtitle1">
          {t("dashboard", "liveFormEditor.stepColor.selectColor")}
        </DashboardTypography>

        {/* All choices for color*/}
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          {LIVE_EDITOR_PRESET_COLORS.map((color) => (
            <LiveEditorColorButton
              key={color}
              color={color}
              isSelected={formColor === color && !isGradientSelected}
              onClick={() => handleColorChange(color)}
            />
          ))}

          {/* Dull Color */}
          <LiveEditorColorButton
            isDiagonal
            isSelected={formColor === LIVE_EDITOR_BLACK_COLOR && !isGradientSelected}
            onClick={() => handleColorChange(LIVE_EDITOR_BLACK_COLOR)}
          />

          {/* Gradient Color for custom color */}
          <Box ref={gradientButtonRef}>
            <LiveEditorColorButton
              isGradient
              isSelected={isGradientSelected}
              onClick={handleGradientClick}
              data-test="gradient-color-button"
            />
          </Box>
        </Stack>

        {/* Custom Color Picker Tooltip */}
        {showColorPicker && (
          <ClickAwayListener onClickAway={handleClickAway}>
            <Box
              ref={colorPickerRef}
              sx={{
                position: "absolute",
                zIndex: LIVE_FORM_Z_INDEX.TOP_BAR + 1,
                top: gradientButtonRef.current
                  ? gradientButtonRef.current.offsetTop + gradientButtonRef.current.offsetHeight + 8
                  : "auto",
                left: 0,
                right: 0,
                mx: "auto",
                p: 3,
                borderRadius: 1,
                boxShadow: 3,
                bgcolor: "background.paper",
                width: { xs: "100%", sm: 412 },
                maxWidth: "100%",
              }}
            >
              {/* White pointer */}
              <DashboardIcon
                sx={{
                  position: "absolute",
                  top: -10,
                  left: pointerOffset,
                  transform: "translateX(-50%)",
                  width: 20,
                  height: 10,
                }}
              >
                <Pointer />
              </DashboardIcon>

              <Stack gap={3}>
                <Stack gap={0.5}>
                  <DashboardTypography variant="subtitle1" vibe="text-neutral-moderate">
                    {t("dashboard", "liveFormEditor.stepColor.customColor")}
                  </DashboardTypography>
                  <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                    {t("dashboard", "liveFormEditor.stepColor.customColorDescription")}
                  </DashboardTypography>
                </Stack>

                <Box sx={{ width: "100%" }} data-test="hue-picker-slider">
                  <ReactColorHuePicker
                    color={customTempColor}
                    onChange={handlePickerSlide}
                    onChangeComplete={handlePickerChangeCompleted}
                    width="100%"
                  />
                </Box>

                <OrDivider />

                <DashboardTextField
                  label={t("dashboard", "liveFormEditor.stepColor.hexValue")}
                  value={customTempColor.replace("#", "# ").toUpperCase()}
                  onChange={(e) => {
                    let value = e.target.value.trim().toUpperCase();
                    if (!value.startsWith("#")) {
                      value = "#" + value;
                    }
                    value = value.replace(/^#\s*/, "#");
                    setCustomTempColor(value);
                  }}
                  error={isInvalidHex}
                  errorMessage={isInvalidHex ? t("dashboard", "liveFormEditor.stepColor.hexValue.invalid") : undefined}
                  sx={{ mb: 2 }}
                />

                <Stack direction="row" justifyContent="flex-end" gap={1}>
                  <DashboardButton variant="outlined" onClick={handleCancelCustomColor}>
                    {t("dashboard", "liveFormEditor.stepColor.cancel")}
                  </DashboardButton>
                  <DashboardButton
                    variant="filled"
                    vibe="brand"
                    onClick={handleApplyCustomColor}
                    data-test="apply-custom-color-button"
                  >
                    {t("dashboard", "liveFormEditor.stepColor.apply")}
                  </DashboardButton>
                </Stack>
              </Stack>
            </Box>
          </ClickAwayListener>
        )}
      </Stack>
    </LiveEditorBlock>
  );
};
